import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { SelectableCardList, type SelectableCardData } from '~/widgets'
import {
  allowTransportType,
  defaultPackageConstraint,
  type ReturnPortal,
  type CountryCode,
  type TransportType,
} from '~/model'
import { insertIf, formatNumber } from '~/helpers'

type ItemType = SelectableCardData<TransportType>

export const getTransportTypeItems = (
  returnPortal: ReturnPortal,
  countryCode: CountryCode,
  t: TFunction
): [ItemType[], TransportType | undefined] => {
  const showParcelItem = allowTransportType(returnPortal, 'parcel', countryCode)
  const showPalletItem = allowTransportType(returnPortal, 'pallet', countryCode)
  const showParcelItemDescription = returnPortal.businessUnitCode === 'ccc'
  const items = [
    ...insertIf<ItemType>(showParcelItem, {
      label: t('parcel'),
      description: showParcelItemDescription
        ? t('tillxKg', { weight: formatNumber(defaultPackageConstraint.weight) })
        : undefined,
      value: 'parcel',
      iconName: 'ParcelTypesMedium',
    }),
    ...insertIf<ItemType>(showPalletItem, {
      label: t('pallet'),
      value: 'pallet',
      iconName: 'Pallet',
    }),
  ]
  const defaultValue = items.length === 1 ? items[0].value : undefined
  return [items, defaultValue]
}

type Props = {
  returnPortal: ReturnPortal
  countryCode: CountryCode
  showRequiredDataLabel: boolean
}

export const TransportTypeInput = ({ returnPortal, countryCode, showRequiredDataLabel }: Props) => {
  const { t } = useTranslation()
  const [items] = getTransportTypeItems(returnPortal, countryCode, t)

  return (
    <SelectableCardList<TransportType>
      fieldName="transportType"
      showRequiredDataLabel={showRequiredDataLabel}
      title={t('choosePackageType')}
      items={items}
    />
  )
}
